import { FieldData, Select, TextField, useForm } from 'vopay';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ACCOUNT_TYPE_BY_FILTER,
  ACCOUNT_TYPE_FILTER_OPTIONS,
  AccountSearchParams,
  CURRENCY_FILTER_OPTIONS,
} from '../types';
import useAccountTypeLabels from '../useAccountTypeLabels';

export type UseAccountSearchFormConfigParams = {
  /** The filters currently in the URL. Re-seeded on change, so Back and a cleared filter both stick. */
  initialValues: AccountSearchParams;
  onSubmit: (searchParams: AccountSearchParams) => void;
};

export default function useAccountSearchFormConfig({ initialValues, onSubmit }: UseAccountSearchFormConfigParams) {
  const config = useConfig();

  const { renderFormElements, ctx } = useForm<AccountSearchParams>({
    initialValues,
    config,
    onSubmit,
    // The parent feeds these from the URL's query params, which change without a remount — browser
    // Back being the obvious case. Without this the fields keep whatever was last typed and the next
    // Search re-submits it.
    enableReinitialize: true,
  });

  return {
    ctx,
    renderFormElements: () => renderFormElements({ config }),
  };
}

function useConfig(): FieldData<AccountSearchParams>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.AccountSearchForm' });
  const { t: tPlaceholders } = useTranslation('translations', { keyPrefix: 'Shared.Placeholders' });
  const { getAccountTypeLabel } = useAccountTypeLabels();

  // The type options' labels come from the account-type resolver rather than their own copies of the
  // same two strings — one place decides how a row's Type reads, whether it's rendered in the table,
  // the drawer or this filter.
  const typeOptions = useMemo(
    () => [
      { value: 'All', label: t('TypeOptions.all', { defaultValue: 'All' }) },
      ...ACCOUNT_TYPE_FILTER_OPTIONS.map((value) => ({
        value,
        label: getAccountTypeLabel(ACCOUNT_TYPE_BY_FILTER[value]),
      })),
    ],
    [t, getAccountTypeLabel],
  );

  const currencyOptions = useMemo(
    () => [
      { value: 'All', label: t('CurrencyOptions.all', { defaultValue: 'All' }) },
      ...CURRENCY_FILTER_OPTIONS.map((value) => ({ value, label: value })),
    ],
    [t],
  );

  return [
    {
      fieldName: 'type',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => (
        <Select
          version="2"
          {...getFormikData<AccountSearchParams['type']>({})}
          sort={false}
          placeholder={tPlaceholders('type', { defaultValue: 'Type' })}
          options={typeOptions}
        />
      ),
    },
    {
      fieldName: 'currency',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => (
        <Select
          version="2"
          {...getFormikData<AccountSearchParams['currency']>({})}
          sort={false}
          placeholder={tPlaceholders('currency', { defaultValue: 'Currency' })}
          options={currencyOptions}
        />
      ),
    },
    {
      fieldName: 'searchTerm',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => (
        <TextField
          variant="outlineShadow"
          {...getFormikData<AccountSearchParams['searchTerm']>({})}
          placeholder={tPlaceholders('keyword', { defaultValue: 'Keyword' })}
        />
      ),
    },
  ];
}
